import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Mail, Lock, Loader2, Eye, EyeOff, User, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    role: "",
    location: "",
    company: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    // Fetch available roles
    const fetchRoles = async () => {
      try {
        const response = await axios.get(`${API}/roles`);
        setRoles(response.data.roles);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        // Fallback roles
        setRoles([
          "Music Supervisor", "Label", "Publisher", "CMO", 
          "Artist", "Songwriter", "Producer", "Brand", "A&R", "Manager"
        ]);
      }
    };
    fetchRoles();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.password || !formData.name || !formData.role) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        location: formData.location || null,
        company: formData.company || null
      });
      
      if (response.data.success) {
        login(response.data.token, response.data.user);
        toast.success(`Welcome to SyncChain, ${response.data.user.name}!`);
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden py-8"
      data-testid="register-page"
    >
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1754557177750-15e25cf42ff6?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-syncchain-bg via-syncchain-bg/95 to-syncchain-bg" />
      
      {/* Glow Effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-syncchain-accent/10 rounded-full blur-[150px]" />

      {/* Register Card */}
      <Card className="w-full max-w-md mx-4 bg-syncchain-paper/80 backdrop-blur-xl border-white/10 relative z-10 animate-slide-up">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_87e61a64-90d2-4c20-8ba6-31392dc5e379/artifacts/xzzkl8ic_SyncChain_White.png" 
              alt="SyncChain Logo" 
              className="h-12 w-auto mx-auto"
            />
          </div>
          <CardDescription className="text-syncchain-text-secondary font-mono text-xs uppercase tracking-wider">
            Create Your Account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Full Name *
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="John Smith"
                  className="pl-10 h-11 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Email *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10 h-11 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Role */}
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Your Role *
              </Label>
              <Select value={formData.role} onValueChange={(value) => handleChange("role", value)}>
                <SelectTrigger className="h-11 bg-syncchain-surface border-white/10 text-syncchain-text" data-testid="role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Company & Location Row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                  Company
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                  <Input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleChange("company", e.target.value)}
                    placeholder="Company name"
                    className="pl-10 h-11 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                    data-testid="company-input"
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                  Location
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    placeholder="City, Country"
                    className="pl-10 h-11 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                    data-testid="location-input"
                  />
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Password *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Min 6 characters"
                  className="pl-10 pr-10 h-11 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-syncchain-text-muted hover:text-syncchain-text"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                Confirm Password *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-syncchain-text-muted" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  placeholder="Confirm password"
                  className="pl-10 h-11 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 bg-syncchain-accent hover:bg-syncchain-accent-hover text-white font-semibold rounded-full transition-all duration-300 glow-hover mt-2"
              disabled={loading}
              data-testid="register-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <p className="text-sm text-syncchain-text-secondary">
              Already have an account?{" "}
              <Link 
                to="/login" 
                className="text-syncchain-accent hover:text-syncchain-accent-hover font-medium"
              >
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
